import styled, { css } from 'styled-components';

import { IconButton, TextButton } from '@xingternal/button';
import { Card } from '@xingternal/card';
import { Flag } from '@xingternal/flag';
import { IconLike, IconSparkle } from '@xingternal/icons';
import { mediaConfined } from '@xingternal/layout-tokens';
import {
  scale110,
  spaceL,
  spaceM,
  spaceS,
  spaceXL,
  spaceXXL,
  xdlColorText,
  xdlColorTextInvert,
  xdlColorTextSecondary,
} from '@xingternal/tokens';
import { Headline } from '@xingternal/typography';

export const JobSummaryCard = styled(Card)`
  width: 100%;
  padding: ${spaceL} ${spaceXL};
`;

export const JobSummaryCardContent = styled.div<{ $collapsed?: boolean }>`
  display: inline-grid;
  grid-template-columns: ${spaceL} 1fr 1fr 4fr;
  grid-template-areas:
    'flag    flag     flag     flag'
    'icon    headline headline headline'
    ${({ $collapsed }) => ($collapsed ? null : `'notice notice notice notice'`)}
    'content content  content  content'
    ${({ $collapsed }) => ($collapsed ? null : `'feedback feedback feedback feedback'`)};
  gap: ${spaceM};
  padding-right: ${({ $collapsed }) => ($collapsed ? spaceXL : 0)};

  @media ${mediaConfined} {
    grid-template-areas:
      'icon headline flag .'
      '.    content  content content'
      ${({ $collapsed }) => ($collapsed ? null : `'notice notice notice feedback'`)};
  }
`;

export const JobSummaryIcon = styled(IconSparkle)`
  grid-area: icon;
  align-self: center;
`;

export const JobSummaryHeadline = styled(Headline)`
  grid-area: headline;
  text-wrap: nowrap;
`;

export const NewFlag = styled(Flag)`
  grid-area: flag;
  width: fit-content;
  align-self: center;
`;

export const ContentWrapper = styled.div`
  grid-area: content;
`;

const JobSummaryMetaWrapper = styled.div`
  margin-bottom: ${spaceS};

  @media ${mediaConfined} {
    margin-bottom: 0;
    margin-top: ${spaceL};
  }
`;

export const JobSummaryNotice = styled(JobSummaryMetaWrapper)`
  grid-area: notice;
`;

export const JobSummaryFeedback = styled(JobSummaryMetaWrapper)`
  grid-area: feedback;
  display: flex;
  flex-direction: row;
  gap: ${spaceS};
  justify-content: start;
  margin-bottom: ${spaceXL};

  @media ${mediaConfined} {
    justify-content: end;
    margin-bottom: 0;
  }
`;

export const FeedbackButton = styled(TextButton).attrs<{ $action: 'like' | 'unlike' }>(() => ({
  icon: IconLike,
}))`
  transform: rotate(${({ $action }) => ($action === 'unlike' ? '180deg' : '0')});
  color: ${xdlColorTextSecondary};
  height: ${scale110};
  min-width: ${scale110};
`;

export const JobSummaryExpandButton = styled(IconButton)<{ $collapsed?: boolean }>`
  position: absolute;
  transition: transform 0.3s ease;
  top: ${spaceXXL};
  right: ${spaceL};
  /* This is to avoid the tap highlight color on mobile */
  -webkit-tap-highlight-color: transparent;

  @media ${mediaConfined} {
    top: ${spaceL};
  }

  ${({ $collapsed }) =>
    $collapsed
      ? css`
          transform: rotate(-90deg);

          @media ${mediaConfined} {
            transform: rotate(0deg);
          }
        `
      : css`
          transform: rotate(-180deg);
          top: auto;
          bottom: 0;
          right: calc(50% - 21px);

          @media ${mediaConfined} {
            top: ${spaceL};
            right: ${spaceL};
          }
        `}

  &:hover::after {
    background-color: transparent;
  }

  &:active {
    color: ${xdlColorTextInvert};
  }

  &:active:hover {
    background-color: transparent;
    color: ${xdlColorText};
  }
`;
